import type { SpeciesInformation } from "./server/lib/types";

export const AGENT_TOOLS = {
  SEARCH_SPECIES: "searchSpecies",
  GET_SPECIES_GROWTH_DETAILS: "getSpeciesGrowthDetails",
} as const;

export const SCAN_EVENT = {
  STAGE: "stage",
  ROUND_START: "round-start",
  TOOL_CALL: "tool-call",
  TOOL_RESULT: "tool-result",
  FINAL: "final",
  DONE: "done",
  ERROR: "error",
} as const;

export type Garden = {
  pots: Pot[];
  location: Location | null;
}

export type Location = {
  id: string;
  name: string;
  region: string;
  lat: number;
  lon: number;
}

export type Pot = {
  id: string;
  name: string;
  shape: PotShape | string;
  size: PotSize;
  sunHours?: {
    start: string;
    end: string;
  };
  plants: Plant[];
  timeline: PotTimeline;
}

export type Plant = {
  id: string;
  nickname: string;
  species?: Species;
  plantedOn: string;
  photoImageUrl: string | null;
}

export type PotShape = "terracotta" | "basket" | "concrete" | "long";

export type PotSize = "small" | "medium" | "large";

export type WateringDay = {
  date: string;
  shouldWater: boolean;
}

export type WateringPlan = {
  today: WateringDay;
  oneDayLater: WateringDay;
  reason: string;
}

export type PotTimelineItem = {
  date: string;
  watered?: "user" | "rain";
}

export type TimelineKey = "twoDaysAgo" | "oneDayAgo" | "today" | "oneDayLater" | "twoDaysLater";

export type PotTimeline = Record<TimelineKey, PotTimelineItem>;

export const TIMELINE_OFFSETS: ReadonlyArray<{ key: keyof PotTimeline; offset: number }> = [
  { key: "twoDaysAgo", offset: -2 },
  { key: "oneDayAgo", offset: -1 },
  { key: "today", offset: 0 },
  { key: "oneDayLater", offset: 1 },
  { key: "twoDaysLater", offset: 2 },
];

const TIMELINE_KEY_BY_OFFSET = new Map(TIMELINE_OFFSETS.map((e) => [e.offset, e.key] as const));

export function timelineKeyForOffset(offset: number): keyof PotTimeline | null {
  return TIMELINE_KEY_BY_OFFSET.get(offset) ?? null;
}

export interface WaterEntry {
  wateredOn: string;            // ISO date YYYY-MM-DD
  source: "user" | "rain";
  createdAt: string;
  note: string | null;
}

export type WeatherCondition =
  | "sun" | "night" | "cloud" | "rain" | "storm"
  | "heat" | "cold" | "wind" | "frost" | "mist";

export interface DayForecast {
  /** Days from today: past days negative, future positive. */
  date: string;
  sunrise: string;
  sunset: string;
  condition: WeatherCondition
  hours: {
    iso: string;
    label: string;
    condition: WeatherCondition;
    isDay: boolean;
    tempC: number;
    humidity: number;
    precipitationMm: number;
    windKmh: number;
    uvIndex: number;
    // Number of sunshine seconds per hour quarter
    sunshine: {
      q1: number;
      q2: number;
      q3: number;
      q4: number;
    }
  }[];
}

export type Species = {
  "id": string;
  labelImageUrl?: string | null;
  color: string;
  initial: string;
  firstSeenAt: string;
  lastSeenAt: string;
} & SpeciesInformation;

export type WeatherForCurrent5DaysWindow = Record<TimelineKey, DayForecast>;